/* eslint-disable arrow-body-style */
import {inject} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {catchError, concatMap, exhaustMap, filter, map, mergeMap, of, switchMap} from "rxjs";
import {AssetValuationService} from "../../services/asset-valuation.service";
import {AssetActions, AssetValuationActions, AssetValuationDetailActions, AssetValuationWriteoffActions, FinancialPerformanceRecordsActions} from "../asset.actions";
import {Store} from "@ngrx/store";
import {selectGeneralPartnerQualityCode, selectRelatedFundValuationId, selectSelectedAssetId} from "../asset/asset.selectors";
import {
    selectActiveAssetValuation,
    selectActiveCalculatedScenarios,
    selectActiveCalculatedValuation,
    selectActiveScenarios,
    selectPreferredAssetValuationForAssetAndFundValuation
} from "./asset-valuation.selectors";
import {DiscountMultipleValuation, isGpQuality, isValidValuationInput} from "../../models/asset-valuation/discount-multiple-valuation";
import {AssetValuationTypeEnum} from "../../models/asset-valuation/asset-valuation-type.enum";
import {FinancialOperationalValuation} from "../../models/asset-valuation/financial-operational-valuation";
import {isOfScenarioType} from "../../../shared/model/scenario-type";
import {EMPTY_CODE} from "../../../shared/model/traceable";
import {AssetValuationWithScenarios} from "../../models/asset-valuation/asset-valuation-with-scenario";
import {ErrorHandlingUtil} from "../../../shared/utils/error-handling-util";
import {concatLatestFrom} from "@ngrx/operators";
import {EMPTY_ASSET_VALUATION} from "../../models/asset-valuation/asset-valuation";

export const loadAllAssetValuationsForAssetAndFundValuation = createEffect(
    (actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(AssetValuationActions.loadAllForAssetAndFundValuation),
            switchMap((action) =>
                assetValuationService.getAssetValuationsForAssetAndFundValuation(action.assetId, action.fundValuationId).pipe(
                    map((valuations) => AssetValuationActions.loadAllForAssetAndFundValuationSuccess({assetId: action.assetId, valuations})),
                    catchError((errorResponse: any) => of(AssetValuationActions.loadAllForAssetAndFundValuationError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const autoLoadPreferredValuationDetail = createEffect(
    (actions$ = inject(Actions), store$ = inject(Store)) => {
        return actions$.pipe(
            ofType(AssetValuationActions.loadAllForAssetAndFundValuationSuccess),
            concatLatestFrom(() => store$.select(selectActiveAssetValuation)),
            map(([action, currentValuation]) => {
                const currentId = currentValuation.data.id;
                if (currentId && action.valuations.some(v => v.id === currentId)) {
                    return AssetValuationDetailActions.loadDetail({assetId: action.assetId, assetValuationId: currentId});
                }
                const preferred = action.valuations.find(v => v.preferredValuation);
                const dmv = action.valuations.find(v => v.valuationType.code === AssetValuationTypeEnum.DiscountMultipleValuation);
                const target = preferred ?? dmv ?? action.valuations[0];
                if (target?.id) {
                    return AssetValuationDetailActions.loadDetail({assetId: action.assetId, assetValuationId: target.id});
                }
                return AssetValuationDetailActions.loadDetailError({errorMsg: "No asset valuation found"});
            })
        );
    },
    {functional: true}
);

export const loadValuationDetail = createEffect(
    (actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(AssetValuationDetailActions.loadDetail),
            switchMap((action) =>
                assetValuationService.getAssetValuationDetail(action.assetId, action.assetValuationId).pipe(
                    map((avWithScenarios) => AssetValuationDetailActions.loadDetailSuccess({
                        assetValuation: avWithScenarios.assetValuation,
                        scenarios: avWithScenarios.scenarios ?? []
                    })),
                    catchError((errorResponse: any) => of(AssetValuationDetailActions.loadDetailError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const changePreferedValuation = createEffect(
    (actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(AssetValuationActions.changePreferedValuation),
            exhaustMap((action) => {
                if (action.assetId === undefined || action.assetValuationId === undefined) {
                    return of(AssetValuationActions.changePreferedValuationError({errorMsg: "Could not set the calculation base, please try again."}));
                }
                return assetValuationService.updatePreferedValuation(action.assetId, action.assetValuationId).pipe(
                    map((assetValuations) => AssetValuationActions.changePreferedValuationSuccess({assetValuations})),
                    catchError((errorResponse: any) => of(AssetValuationActions.changePreferedValuationError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

// US-1189: records the user's write-off confirmation for an asset without forecast cashflow.
export const confirmAssetValuationWriteoff = createEffect(
    (actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(AssetValuationWriteoffActions.confirm),
            mergeMap((action) =>
                assetValuationService.confirmWriteoff(action.assetId, action.assetValuationId, action.confirmed).pipe(
                    map((assetValuation) => AssetValuationWriteoffActions.confirmSuccess({assetValuation})),
                    catchError((errorResponse: any) => of(AssetValuationWriteoffActions.confirmError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const loadDetailAfterChangingPreferred = createEffect(
    (actions$ = inject(Actions), store$ = inject(Store)) => {
        return actions$.pipe(
            ofType(AssetValuationActions.changePreferedValuationSuccess),
            concatLatestFrom(() => store$.select(selectSelectedAssetId)),
            map(([action, assetId]) => {
                const newPreferred = action.assetValuations.find(v => v.preferredValuation);
                if (newPreferred?.id && assetId) {
                    return AssetValuationDetailActions.loadDetail({assetId, assetValuationId: newPreferred.id});
                }
                return AssetValuationActions.doNothing();
            })
        );
    },
    {functional: true}
);

export const saveAssetValuationWithScenarios = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(AssetValuationDetailActions.save),
            concatLatestFrom(() => [store$.select(selectSelectedAssetId), store$.select(selectRelatedFundValuationId)]),
            exhaustMap(([action, assetId, fundValuationId]) => {
                if (assetId === undefined || fundValuationId === undefined) {
                    return of(AssetActions.loadError({errorMsg: "Error while loading the asset or fund valuation, please refresh the page"}));
                }
                if (!action.assetValuation.id) {
                    return of(AssetValuationDetailActions.saveError({errorMsg: "Cannot save: missing asset valuation id, please refresh"}));
                }
                const assetValuationWithScenarios: AssetValuationWithScenarios = {
                    assetValuation: action.assetValuation,
                    scenarios: action.scenarios,
                    ...(action.pendingHistoryBaseChange ? {pendingHistoryBaseChange: action.pendingHistoryBaseChange} : {})
                };
                const shouldResetValuation = action.resetValuation === true;

                return assetValuationService.save(assetId, fundValuationId, action.assetValuation.id, assetValuationWithScenarios).pipe(
                    mergeMap((avWithScenarios) => {
                        const successAction = AssetValuationDetailActions.saveSuccess({
                            assetValuation: avWithScenarios.assetValuation,
                            scenarios: avWithScenarios.scenarios
                        });

                        if (shouldResetValuation && avWithScenarios.assetValuation.id) {
                            return [
                                successAction,
                                AssetValuationDetailActions.resetValuation({assetId, assetValuationId: avWithScenarios.assetValuation.id})
                            ];
                        }
                        return [successAction];
                    }),
                    catchError((error: any) => of(AssetValuationDetailActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(error)})))
                );
            })
        );
    },
    {functional: true}
);

export const saveAssetValuationRationale = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(AssetValuationDetailActions.saveRationale),
            concatLatestFrom(() => [store$.select(selectSelectedAssetId), store$.select(selectRelatedFundValuationId)]),
            exhaustMap(([action, assetId, fundValuationId]) => {
                if (assetId === undefined || fundValuationId === undefined) {
                    return of(AssetActions.loadError({errorMsg: "Error while loading the asset or fund valuation, please refresh the page"}));
                }
                return assetValuationService.saveRationale(assetId, action.assetValuationId, fundValuationId, action.rationale).pipe(
                    map((assetValuation) => AssetValuationDetailActions.saveRationaleSuccess({assetValuation})),
                    catchError((errorResponse: any) => of(AssetValuationDetailActions.loadDetailError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const calcDiscountMultipleValuation = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(AssetValuationDetailActions.calcDMV),
            concatLatestFrom(() => [store$.select(selectSelectedAssetId), store$.select(selectGeneralPartnerQualityCode)]),
            exhaustMap(([action, assetId, generalPartnerQualityCode]) => {
                const assetValuationId = action.assetValuation.id;
                if (assetId === undefined || assetValuationId === undefined) {
                    return of(AssetValuationDetailActions.calcDMVError({errorMsg: "No valuation for this asset available, please add a valuation first"}));
                }
                const discountMultipleValuation: DiscountMultipleValuation = {
                    assetValuation: action.assetValuation,
                    scenarios: action.scenarios,
                    valuationInput: isValidValuationInput(action.valuationInput) ? action.valuationInput : "MULTIPLE",
                    gpQuality: isGpQuality(generalPartnerQualityCode) ? generalPartnerQualityCode : undefined
                };
                return assetValuationService.calcDMV(assetId, assetValuationId, discountMultipleValuation, action.withAlgorithm).pipe(
                    map((dmv) => (action.save) ?
                        AssetValuationDetailActions.save({assetValuation: dmv.assetValuation, scenarios: dmv.scenarios}) :
                        AssetValuationDetailActions.calcDMVSuccess({assetValuation: dmv.assetValuation, scenarios: dmv.scenarios, confidenceZone: dmv.confidenceZone})),
                    catchError((errorResponse: any) => of(AssetValuationDetailActions.calcDMVError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const calcOperationalValuation = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(AssetValuationDetailActions.calcFOV),
            concatLatestFrom(() => [store$.select(selectSelectedAssetId), store$.select(selectActiveCalculatedScenarios)]),
            exhaustMap(([action, assetId, scenarios]) => {
                const assetValuationId = action.assetValuation.id;
                if (assetId === undefined || assetValuationId === undefined) {
                    return of(AssetValuationDetailActions.calcFOVError({errorMsg: "No valuation for this asset available, please add a valuation first"}));
                }
                const financialOperationalValuation: FinancialOperationalValuation = {
                    assetValuation: action.assetValuation,
                    scenarioTypeForCashflowCalc: {...EMPTY_CODE, code: action.scenarioType},
                    scenarios,
                    financialPerformanceRecords: action.financialPerformanceRecords
                };
                return assetValuationService.calcFOV(assetId, assetValuationId, financialOperationalValuation).pipe(
                    map((operational) => {
                        if (operational.scenarioTypeForCashflowCalc.code && isOfScenarioType(operational.scenarioTypeForCashflowCalc.code)) {
                            return AssetValuationDetailActions.calcFOVSuccess({
                                assetValuation: operational.assetValuation,
                                scenarios: operational.scenarios,
                                financialPerformanceRecords: operational.financialPerformanceRecords,
                                scenarioType: operational.scenarioTypeForCashflowCalc.code
                            });
                        } else {
                            return AssetValuationDetailActions.calcFOVError({
                                errorMsg: `Error while calculating the ${operational.scenarioTypeForCashflowCalc.code} case scenario, please refresh the page`,
                                scenarioType: action.scenarioType
                            });
                        }
                    }),
                    catchError((error: any) => of(AssetValuationDetailActions.calcFOVError({
                        errorMsg: ErrorHandlingUtil.getErrorMessage(error),
                        scenarioType: action.scenarioType
                    })))
                );
            })
        );
    },
    {functional: true}
);

export const createAssetValuationFOV = createEffect(
    (actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(AssetValuationDetailActions.create),
            exhaustMap((action) => {
                if (action.assetId && action.fundValuationId) {
                    return assetValuationService.create(
                        action.assetId,
                        action.fundValuationId,
                        {
                            ...EMPTY_ASSET_VALUATION,
                            valuationType: {
                                ...EMPTY_ASSET_VALUATION.valuationType,
                                code: action.valuationType
                            }
                        }
                    ).pipe(
                        map((avWithScenarios) => AssetValuationDetailActions.createSuccess({
                            assetId: action.assetId,
                            fundValuationId: action.fundValuationId,
                            assetValuation: avWithScenarios.assetValuation
                        })),
                        catchError((errorResponse: any) => of(AssetValuationDetailActions.loadDetailError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(AssetValuationDetailActions.loadDetailError({errorMsg: "Error while loading the asset or fund valuation, please refresh the page"}));
                }
            })
        );
    },
    {functional: true}
);

export const loadAllAssetValuationsAfterCreatingFOV = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AssetValuationDetailActions.createSuccess),
            map((action) => AssetValuationActions.loadAllForAssetAndFundValuation({assetId: action.assetId, fundValuationId: action.fundValuationId}))
        );
    },
    {functional: true}
);

export const saveAssetValuationScenarioCashflowsFOV = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(AssetValuationDetailActions.saveCashflows),
            concatLatestFrom(() => [store$.select(selectSelectedAssetId), store$.select(selectActiveScenarios)]),
            exhaustMap(([action, assetId, scenarios]) => {
                if (assetId === undefined || action.assetValuationId === undefined || action.scenario === undefined) {
                    return of(AssetValuationDetailActions.loadDetailError({errorMsg: "Error while loading the asset valuation scenarios, please refresh the page"}));
                }
                return assetValuationService.saveScenario(assetId, action.assetValuationId, action.scenario).pipe(
                    map((updatedScenario) => {
                        const updatedScenarios = scenarios.map(scenario => scenario.id === updatedScenario.id ? updatedScenario : scenario);
                        return AssetValuationDetailActions.saveAllScenariosSuccess({scenarios: updatedScenarios})
                    }),
                    catchError((errorResponse: any) => of(AssetValuationDetailActions.loadDetailError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

/**
 * US-1189: entering forecast cashflow ends a confirmed write-off, and the server clears the flag on the
 * scenario endpoint too — but that response carries only the scenario, so nothing in the store learns about
 * it. Re-read the valuation list so the forecast card's write-off note disappears with the save instead of
 * only after leaving and re-entering the page. Gated on a standing confirmation: ordinary cashflow edits
 * (the overwhelming majority) cost no extra request.
 */
export const reloadValuationsAfterCashflowEndsWriteoff = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AssetValuationDetailActions.saveAllScenariosSuccess),
            concatLatestFrom(() => [
                store$.select(selectPreferredAssetValuationForAssetAndFundValuation),
                store$.select(selectSelectedAssetId),
                store$.select(selectRelatedFundValuationId)
            ]),
            filter(([, preferredValuation, assetId, fundValuationId]) =>
                preferredValuation?.isWriteoff?.value === true && assetId !== undefined && fundValuationId !== undefined),
            map(([, , assetId, fundValuationId]) => AssetValuationActions.loadAllForAssetAndFundValuation({
                assetId: assetId as number,
                fundValuationId: fundValuationId as number
            }))
        );
    },
    {functional: true}
);

export const copyFOVFinancialPerformanceRecords = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(AssetValuationDetailActions.copyFinancialPerformanceRecords),
            concatLatestFrom(() => [store$.select(selectSelectedAssetId), store$.select(selectActiveCalculatedScenarios)]),
            mergeMap(([action, assetId, scenarios]) => {
                const sourceScenarioId = scenarios.find(s => s.scenario.code === action.sourceScenarioType)?.id;
                const targetScenarioId = scenarios.find(s => s.scenario.code === action.targetScenarioType)?.id;

                if (assetId === undefined || action.assetValuationId === undefined || sourceScenarioId === undefined || targetScenarioId === undefined) {
                    return of(AssetValuationDetailActions.copyFinancialPerformanceRecordsError({
                        errorMsg: "Error while copying the asset valuation, please refresh the page",
                        scenarioType: action.targetScenarioType
                    }));
                }
                return assetValuationService.copyFinancialPerformanceFromScenario(assetId, action.assetValuationId, targetScenarioId, sourceScenarioId).pipe(
                    map((financialPerformanceRecords) =>
                        AssetValuationDetailActions.copyFinancialPerformanceRecordsSuccess({
                            assetId,
                            assetValuationId: action.assetValuationId,
                            financialPerformanceRecords,
                            targetScenarioId: targetScenarioId,
                            targetScenarioType: action.targetScenarioType
                        })),
                    catchError((errorResponse: any) => of(AssetValuationDetailActions.copyFinancialPerformanceRecordsError({
                        errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse),
                        scenarioType: action.targetScenarioType
                    })))
                );
            })
        );
    },
    {functional: true}
);

export const loadAssetValAfterCopyFOVFinancialPerformanceRecordsSuccess = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AssetValuationDetailActions.copyFinancialPerformanceRecordsSuccess),
            map((action) => AssetValuationDetailActions.loadDetail({assetId: action.assetId, assetValuationId: action.assetValuationId}))
        );
    },
    {functional: true}
);

export const loadFinancialPerformance = createEffect(
    (actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(FinancialPerformanceRecordsActions.load),
            concatMap((action) => {
                    return assetValuationService.getFinancialPerformanceForScenario(action.assetId, action.assetValuationId, action.scenarioId).pipe(
                        map((financialPerformanceRecords) => {
                            switch (action.scenarioType) {
                                case "LOW":
                                    return FinancialPerformanceRecordsActions.loadLowSuccess({financialPerformanceRecords, scenarioType: action.scenarioType});
                                case "HIGH":
                                    return FinancialPerformanceRecordsActions.loadHighSuccess({financialPerformanceRecords, scenarioType: action.scenarioType});
                                default:
                                    return FinancialPerformanceRecordsActions.loadBaseSuccess({financialPerformanceRecords, scenarioType: action.scenarioType});
                            }
                        }),
                        catchError((errorResponse: any) => of(FinancialPerformanceRecordsActions.loadError({
                            errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse),
                            scenarioType: action.scenarioType
                        })))
                    );
                }
            )
        );
    },
    {functional: true}
);

export const saveAssetValuationAfterPersistingFinancialPerformance = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(FinancialPerformanceRecordsActions.saveWithHistoryBaseSuccess),
            concatLatestFrom(() => [store$.select(selectActiveCalculatedValuation), store$.select(selectActiveCalculatedScenarios)]),
            map(([, assetValuationApi, scenarios]) =>
                AssetValuationDetailActions.save({assetValuation: assetValuationApi.data, scenarios})
            )
        );
    },
    {functional: true}
);

export const calcFinancialPerformance = createEffect(
    (actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(FinancialPerformanceRecordsActions.calc, FinancialPerformanceRecordsActions.edit),
            switchMap((action) => {
                return assetValuationService.calculateFinancialPerformanceForScenario(
                    action.assetId,
                    action.assetValuationId,
                    action.scenarioId,
                    action.financialPerformanceRecords,
                    action.inputMode,
                    action.historyBase
                ).pipe(
                    map((financialPerformanceRecords) => {
                        switch (action.scenarioType) {
                            case "LOW":
                                return FinancialPerformanceRecordsActions.calcSuccessLow({financialPerformanceRecords, scenarioType: action.scenarioType});
                            case "HIGH":
                                return FinancialPerformanceRecordsActions.calcSuccessHigh({financialPerformanceRecords, scenarioType: action.scenarioType});
                            default:
                                return FinancialPerformanceRecordsActions.calcSuccessBase({financialPerformanceRecords, scenarioType: action.scenarioType});
                        }
                    }),
                    catchError((error: any) => of(FinancialPerformanceRecordsActions.calcError({
                        errorMsg: ErrorHandlingUtil.getErrorMessage(error),
                        scenarioType: action.scenarioType
                    })))
                );
            })
        );
    },
    {functional: true}
);

export const triggerOperationalCalcAfterPerformanceRecordsCalc = createEffect(
    (actions$ = inject(Actions), store$ = inject(Store)) => {
        return actions$.pipe(
            ofType(FinancialPerformanceRecordsActions.calcSuccessLow, FinancialPerformanceRecordsActions.calcSuccessBase, FinancialPerformanceRecordsActions.calcSuccessHigh),
            concatLatestFrom(() => store$.select(selectActiveAssetValuation)),
            map(([action, assetValuationApi]) =>
                AssetValuationDetailActions.calcFOV({
                    assetValuation: assetValuationApi.data,
                    financialPerformanceRecords: action.financialPerformanceRecords,
                    scenarioType: action.scenarioType
                })
            )
        );
    },
    {functional: true}
);

export const resetValuation = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(AssetValuationDetailActions.resetValuation),
            concatLatestFrom(() => store$.select(selectRelatedFundValuationId)),
            exhaustMap(([action, fundValuationId]) => {
                if (action.assetId === undefined || action.assetValuationId === undefined || fundValuationId === undefined) {
                    return of(AssetValuationDetailActions.resetValuationError({errorMsg: "Could not reset valuation, please try again."}));
                }
                return assetValuationService.resetValuation(action.assetId, action.assetValuationId).pipe(
                    map(() => AssetValuationDetailActions.resetValuationSuccess({assetId: action.assetId, fundValuationId})),
                    catchError((errorResponse: any) => of(AssetValuationDetailActions.resetValuationError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const reloadAfterResetValuationSuccess = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(AssetValuationDetailActions.resetValuationSuccess),
            mergeMap((action) => [
                AssetValuationActions.loadAllForAssetAndFundValuation({assetId: action.assetId, fundValuationId: action.fundValuationId})
            ])
        );
    },
    {functional: true}
);

export const saveFinancialPerformanceWithHistoryBase = createEffect(
    (actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(FinancialPerformanceRecordsActions.saveWithHistoryBase),
            exhaustMap((action) =>
                assetValuationService.saveFinancialPerformanceWithHistoryBase(
                    action.assetId,
                    action.assetValuationId,
                    action.scenarioId,
                    action.financialPerformanceRecords,
                    action.inputMode,
                    action.historyBase
                ).pipe(
                    map((response) => FinancialPerformanceRecordsActions.saveWithHistoryBaseSuccess({
                        scenarioType: action.scenarioType,
                        historyBase: response.historyBase,
                        forecastRecordsByScenario: response.forecastRecordsByScenario
                    })),
                    catchError((errorResponse: any) => of(FinancialPerformanceRecordsActions.saveWithHistoryBaseError({
                        errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse),
                        scenarioType: action.scenarioType
                    })))
                )
            )
        );
    },
    {functional: true}
);

export const applyQuickForecast = createEffect(
    (actions$ = inject(Actions), assetValuationService = inject(AssetValuationService)) => {
        return actions$.pipe(
            ofType(AssetValuationDetailActions.applyQuickForecast),
            switchMap(({assetId, assetValuationId, scenarioId, scenarioType, earningBase, forecastRecords, seeds, mode}) =>
                assetValuationService.calculateQuickForecast(
                    assetId, assetValuationId, scenarioId,
                    mode, earningBase, seeds, forecastRecords
                ).pipe(
                    map((financialPerformanceRecords) => {
                        switch (scenarioType) {
                            case "LOW":
                                return FinancialPerformanceRecordsActions.calcSuccessLow({financialPerformanceRecords, scenarioType});
                            case "HIGH":
                                return FinancialPerformanceRecordsActions.calcSuccessHigh({financialPerformanceRecords, scenarioType});
                            default:
                                return FinancialPerformanceRecordsActions.calcSuccessBase({financialPerformanceRecords, scenarioType});
                        }
                    }),
                    catchError((error: any) => of(FinancialPerformanceRecordsActions.calcError({
                        errorMsg: ErrorHandlingUtil.getErrorMessage(error),
                        scenarioType
                    })))
                )
            )
        );
    },
    {functional: true}
);
